import { useState } from "react";
import { MapPin, Search } from "lucide-react";
import { errorMessage } from "../../../api/client";
import { venueApi } from "../../../api/resources";
import type { GeocodeCandidate } from "../../../api/types";
import { Button, ErrorText } from "../../../components/ui";

const NOT_FOUND = "Address not found. Place the pin on the map or enter coordinates manually.";
const HINT = "Tip: check the street and city, or wait a few seconds and try again (the free lookup service is rate limited).";

export function AddressLookup({ query, onPick }: { query: string; onPick: (c: GeocodeCandidate) => void }) {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<GeocodeCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lookup = async () => {
    setBusy(true);
    setError(null);
    setResults(null);
    try {
      const res = await venueApi.geocode(query);
      if (res.candidates.length === 0) setError(NOT_FOUND);
      else setResults(res.candidates);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Button type="button" variant="secondary" loading={busy} disabled={query.trim().length < 3} onClick={lookup} data-testid="venue-lookup-address-button">
        <Search className="h-4 w-4" /> Look up address
      </Button>
      <ErrorText message={error} testId="venue-lookup-error" />
      {error === NOT_FOUND && <p data-testid="venue-lookup-hint" className="text-xs text-zinc-500">{HINT}</p>}
      {query && <p data-testid="venue-lookup-query" className="text-xs text-zinc-600">Searching for: {query}</p>}
      {results && (
        <ul data-testid="venue-lookup-results" className="divide-y divide-white/5 rounded-sm border border-white/10">
          {results.map((c, i) => (
            <li key={`${c.latitude}-${c.longitude}-${i}`}>
              <button type="button" onClick={() => { onPick(c); setResults(null); }} data-testid={`venue-lookup-candidate-${i}`}
                className="flex w-full items-start gap-3 px-4 py-3 text-left text-sm text-zinc-300 transition-colors hover:bg-white/5 hover:text-white">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-signal" />
                <span>{c.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
